/**
 * ثبتِ دستیِ حضورِ ماه — یک ردیف برای هر نفر، ستون‌ها کنارِ هم، ویرایشِ دسته‌جمعی.
 *
 * چرا: وقتی دستگاه خراب بوده یا فقط برگهٔ کاغذی داریم، ریزِ روزها لازم نیست؛ فقط
 * «چند روز آمد، چند روز نیامد، چند دقیقه دیر کرد، چند دقیقه اضافه ماند». اگر برای
 * (نفر، ماه) ردیف ثبت شود، موتورِ حقوق همین را می‌خواند و دستگاه را نادیده می‌گیرد.
 * عددِ داخلِ پرانتز همان پیشنهادِ دستگاه است تا فقط اصلاح کنید.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Space, Tag, Tooltip, Typography, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import EditableTable from "../../components/EditableTable";
import GxNote from "../../components/GxNote";
import { getManualMonthAttendance, saveManualMonthAttendance } from "../../api/client";
import { faNum } from "../../utils/faNumberFormat";
import { byCode, byStr } from "../../utils/sorters";

const { Text } = Typography;
const fa = (v) => (v == null || v === "" ? "—" : faNum(v));
const ROLE = { fixed: ["حقوق ثابت", "green"], daily: ["روزمزد", "cyan"] };
const FIELDS = ["present_days", "absence_days", "shortage_minutes", "overtime_minutes"];
/** وضعیتِ دادهٔ دستگاهِ هر نفر در این ماه (v2.110.0) — تا معلوم باشد چه کسی دستی لازم دارد */
const DEVICE = {
  ok: ["دادهٔ دستگاه دارد", "green", "دستگاه برایش تردد دارد؛ فقط اگر اشتباه است دستی ثبت کنید."],
  no_data: ["دستگاه: بدونِ تردد", "red", "کارت دارد ولی این ماه ترددی به او نرسیده (کارتِ مشترک؟ فایل ناقص؟) — دستی ثبت کنید یا نگاشت را اصلاح کنید."],
  no_card: ["کارتِ دستگاه ندارد", "orange", "شناسهٔ دستگاه ندارد — یا در پرسنل کارت بدهید یا دستی ثبت کنید."],
  off: ["غیرحضوری", "default", "تیکِ «حضور و غیاب دستگاه» خاموش است؛ حقوقِ کامل حساب می‌شود مگر اینجا دستی ثبت کنید."],
};

export default function ManualMonthTab({ year, month }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getManualMonthAttendance(year, month);
      setRows((r.data.rows || []).map((x) => ({ ...x, id: x.employee_id })));
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در بارگذاری"); }
    setLoading(false);
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  /** ذخیرهٔ دسته‌جمعی: فقط ردیف‌های تغییرکرده؛ خالی‌شدنِ همهٔ مقدارها = برگشت به دستگاه */
  const saveMany = async (changes) => {
    const payload = changes.map(({ id, ...f }) => {
      const row = rows.find((r) => r.id === id) || {};
      const merged = { ...row, ...f };
      const nothing = FIELDS.every((k) => merged[k] == null || merged[k] === "");
      return { employee_id: id, ...f, clear: nothing };
    });
    const r = await saveManualMonthAttendance(year, month, payload);
    message.success(r.data.message);
    await load();
  };

  const auto = (r, k) => (r.auto && r.auto[k] != null ? r.auto[k] : null);
  const numCol = (title, key, unit) => ({
    title, dataIndex: key, width: 130, align: "center", editType: "number",
    editRule: (v) => (v != null && Number(v) < 0 ? "منفی مجاز نیست" : null),
    render: (v, r) => (
      <Space size={4}>
        <b>{v == null ? <Text type="secondary">—</Text> : fa(v)}</b>
        {auto(r, key) != null && (
          <Tooltip title={`پیشنهادِ دستگاه: ${fa(auto(r, key))} ${unit}`}>
            <Text type="secondary" style={{ fontSize: 11 }}>({fa(auto(r, key))})</Text>
          </Tooltip>
        )}
      </Space>
    ),
  });

  const columns = [
    { title: "کد", dataIndex: "employee_code", width: 70, sorter: byCode("employee_code"), defaultSortOrder: "ascend" },
    { title: "پرسنل", dataIndex: "employee_name", width: 170, sorter: byStr("employee_name"), fixed: "right",
      render: (v, r) => (
        <Space size={4}>
          <span>{v}</span>
          {r.has_manual && <Tag color="gold" style={{ margin: 0 }}>دستی</Tag>}
        </Space>) },
    { title: "دستگاه", dataIndex: "device_state", width: 140, gxFilter: "enum",
      gxFilterGet: (r) => DEVICE[r.device_state]?.[0] || r.device_state,
      render: (v, r) => (
        <Space size={4} wrap>
          <Tooltip title={DEVICE[v]?.[2]}><Tag color={DEVICE[v]?.[1]} style={{ margin: 0 }}>{DEVICE[v]?.[0] || v}</Tag></Tooltip>
          {r.device_user_id != null && <Text type="secondary" style={{ fontSize: 11 }}>کارت {fa(r.device_user_id)}</Text>}
        </Space>) },
    { title: "نوع", dataIndex: "pay_model", width: 90, gxFilter: "enum",
      gxFilterGet: (r) => ROLE[r.pay_model]?.[0] || r.pay_model,
      render: (v) => <Tag color={ROLE[v]?.[1]}>{ROLE[v]?.[0] || v}</Tag> },
    numCol("حضور (روز)", "present_days", "روز"),
    numCol("غیبت (روز)", "absence_days", "روز"),
    numCol("کسرکار / دیرکرد (دقیقه)", "shortage_minutes", "دقیقه"),
    numCol("اضافه‌کاری (دقیقه)", "overtime_minutes", "دقیقه"),
    { title: "یادداشت", dataIndex: "note", width: 180, editType: "text", render: (v) => v || "—" },
    { title: "ثبت‌کننده", dataIndex: "updated_by", width: 110, render: (v) => v || "—" },
  ];

  return (
    <Card title="ثبتِ دستیِ حضورِ ماه — هر نفر یک ردیف"
      extra={<Button icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>}>
      <GxNote id="att-manual-month" signature="v1"
        message="«ویرایش دسته‌جمعی» را بزنید و ستون‌ها را پر کنید: حضور (روز)، غیبت (روز)، کسرکار (دقیقه)، اضافه‌کاری (دقیقه). ردیفی که مقدار داشته باشد در حقوق به‌جای دستگاه حساب می‌شود؛ اگر همهٔ مقدارهایش را خالی کنید دوباره از دستگاه خوانده می‌شود. اضافه‌کاری و کسرکار با نرخِ روزانهٔ همان نفر (روزانه ÷ ساعتِ مبنا) حساب می‌شوند. عددِ داخلِ پرانتز پیشنهادِ دستگاه است." />
      <EditableTable layoutId="att-manual-month" dataSource={rows} columns={columns}
        rowKey="id" loading={loading} onSaveMany={saveMany} size="small"
        scroll={{ x: "max-content" }} />
    </Card>
  );
}
